import css from 'styled-jsx/css'
import { useContext } from 'react'
import { ParcelInfo } from '@ui/types/interfaces'
import { PackageTrackingStatus } from '@ui/components/order/enum'
import { useRedirect } from '@ui/hooks'
import TrackingSteps from '@ui/components/shared/TrackingSteps'
import { DoInfo } from '@ui/components/trackings/do-info'
import { OrderContext } from '@ui/contexts'
import { OrdLineQuantity } from '@ui/components/trackings/quantity/OrdLineQuantity'
import { OrdLineVariant } from '@ui/components/trackings/variant/OrdLineVariant'
import { getDataPackageInfo, getDisplayNumberWithUnitV2 } from '@ui/helpers/packageTracking'

type PackageInfoProps = {
  index: number
  total: number
  dataPackage: ParcelInfo
}

export const PackageInfoTracking: React.FC<PackageInfoProps> = ({ index, total, dataPackage }) => {
  const redirect = useRedirect()
  const { orderDetail } = useContext(OrderContext)

  const {
    status: packageStatus,
    order_lines: orderLines,
    min_do_created_at: paid_date
  } = dataPackage

  const redirectToSelectFlow = (route: string) => {
    redirect(route, { routerOptions: { scroll: false }, query: { ref: 'order-tracking' } })
  }

  const doDisplay = getDataPackageInfo(dataPackage, packageStatus ?? '', redirectToSelectFlow)

  //const parcelsQuantity = orderDetail?.parcel_infos ? orderDetail?.parcel_infos.length : 0
  const totalItems =
    orderLines?.map((o) => o?.quantity ?? 0).reduce((prev, curr) => prev + curr, 0) ?? 0
  const getDisplayNumberItems = getDisplayNumberWithUnitV2(totalItems, 'item')

  return (
    <>
      <style jsx>{compStyle}</style>
      {/* Package header and order lines item */}
      <div className="package-num">
        {total ? (
          <strong className="mb-5">
            Package {index} of {total}
          </strong>
        ) : (
          <strong className="mb-5">Package {index}</strong>
        )}
      </div>

      <div className="package-detail">
        {/* total */}
        {orderDetail?.is_quantity_contract && (
          <div className="mb-2">
            <span>
              This package contains <strong> {getDisplayNumberItems} </strong>
            </span>
          </div>
        )}

        {/* Order Line */}
        {orderLines?.map(
          (orderLine, index) =>
            (packageStatus != PackageTrackingStatus.CANCELLED || !orderLine.reason_created) &&
            (orderDetail?.is_quantity_contract ? (
              <OrdLineQuantity key={index} orderLine={orderLine} />
            ) : (
              <OrdLineVariant key={index} orderLine={orderLine} />
            ))
        )}

        {/* ETA Info */}
        {doDisplay && (
          <>
            <div className="do-info__item mb-4 mt-4 has-pointer">
              {doDisplay.content_items?.map((item, index) => {
                return (
                  <div className="is-flex" key={index}>
                    <div className="text-large has-text-weight-medium content ml-4 mt-3">
                      <p id={`eta-title-idx-${index}`} className="do-info__title">
                        {item.title}
                      </p>
                      <h4 className="do-info__order-code">{item.value}</h4>
                    </div>
                  </div>
                )
              })}
              <div className="is-flex">
                <div className="text-large has-text-weight-medium content ml-4 mt-3">
                  <span className="do-info__title">Need support?{'  '}</span>
                  <a className="is-link" onClick={() => redirectToSelectFlow('/order')}>
                    Contact us
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
        <hr className="mb-20" />

        {/* Status info and description */}
        {doDisplay && doDisplay.status && (
          <div className="is-flex mb-10">
            <div className="text-large has-text-weight-medium content do-status-info">
              <h4 id="status-package-title" className="do-info__order-code">
                {doDisplay.status.title}
              </h4>
              <p id="status-package-description" className="do-info__title">
                {packageStatus == PackageTrackingStatus.UNDELIVERABLE
                  ? doDisplay.status.contentHTML
                  : doDisplay.status.content}
              </p>
            </div>
          </div>
        )}

        {/* Tracking Step*/}
        <TrackingSteps tracking={dataPackage} paid_date={paid_date} />

        {/* Tracking Log*/}
        {dataPackage && <DoInfo tracking={dataPackage} />}
      </div>
    </>
  )
}

const compStyle = css`
  .package-num {
    background-color: #eff7fc;
    padding: 0.75rem 1.5rem;

    font-family: 'Poppins';
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 32px;
  }

  .package-detail {
    padding: 1.5rem;
  }

  @media screen and (max-width: 768px) {
    .package-detail {
      padding: 1rem;
    }
  }

  .is-link {
    font-weight: 600;
    color: #0b92f1;
    cursor: pointer;
  }

  .mb-10 {
    margin-bottom: 10px;
  }

  .mb-20 {
    margin-bottom: 20px;
  }

  .do-status-info {
    h4 {
      font-weight: 600;
      font-size: 17px;
      line-height: 20px;
      color: #000000;
      margin-bottom: 0.2em !important;
    }

    p {
      margin-top: 0px;
      margin-bottom: 0.2em !important;
      font-weight: 400;
    }
  }

  .icon {
    &--inline {
      min-width: 18px;
      margin-left: 7px;
      margin-top: 15px;

      img {
        width: 40px;
        height: 40px;
      }
    }
  }

  .do-info {
    margin-top: 2rem;

    &__order-code {
      font-weight: 600;
      font-size: 17px;
      line-height: 20px;
      color: #000000;
      margin-bottom: 0.2em !important;
    }

    &__title {
      margin-top: 0px;
      margin-bottom: 0.2em !important;
      font-weight: 400;
    }

    &__item {
      background: #f6f6f6;
      box-sizing: border-box;
      border-radius: 5px;
      padding: 0.5rem;
      color: #657381;
    }
  }
`
